import React from 'react';
import { useNavigate } from 'react-router-dom';
import ListHeroPhoto from '../ListHeroPhoto';

/**
 * List card for MyLists page
 * Supports both card and compact views
 */
function DraggableListCard({
  list,
  isCompactView,
  isReorderMode,
  onClick,
}) {
  const navigate = useNavigate();
  const { percentComplete, placeCount, heroItem, heroPlaceId, locationDisplay, categoryDisplay, followersCount, isPrivate, timeSince } = list._computed;
  const placeLabel = placeCount === 1 ? 'place' : 'places';
  const isPinned = list.pinned;
  const hasCollaborators = list.collaborators && list.collaborators.length > 0;

  // Determine privacy icon
  const getPrivacyIcon = () => {
    if (isPrivate) {
      return (
        <svg className="privacy-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      );
    }
    if (list.visibility === 'public' || list.privacyStatus === 'public' || (!list.privacyStatus && !list.visibility && list.isPublic)) {
      return (
        <svg className="privacy-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      );
    }
    if (list.visibility === 'friends_only' || list.privacyStatus === 'friends-only') {
      return (
        <svg className="privacy-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      );
    }
    if (list.visibility === 'shared' || list.privacyStatus === 'specific-friends') {
      return (
        <svg className="privacy-badge-icon" viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M16 21v-2a4 4 0 0 0-4-4H4a4 4 0 0 0-4 4v2"/>
          <circle cx="8" cy="7" r="4"/>
          <rect x="20" y="11" width="10" height="10" rx="1" ry="1"/>
          <path d="M22 11V9a3 3 0 0 1 6 0v2"/>
        </svg>
      );
    }
    return null;
  };

  const handleCardClick = () => {
    if (!isReorderMode) {
      onClick(list.id);
    }
  };

  const handleTripClick = (e) => {
    e.stopPropagation();
    navigate(`/list/${list.id}/trip`);
  };

  return (
    <div
      className="list-drag-wrapper"
      data-list-id={list.id}
    >
      <div
        className={`list-preview-card ${isPinned ? 'pinned' : ''} ${isCompactView ? 'compact' : ''} ${isReorderMode ? 'reorder-mode' : ''}`}
        onClick={handleCardClick}
      >
        {!isCompactView && (
          <div className="list-preview-hero">
            <ListHeroPhoto
              placeId={heroPlaceId}
              altText={list.title || list.name}
              storedPhotoUrl={heroItem?.photoUrl}
              photoUrlSavedAt={heroItem?.photoUrlSavedAt}
              customPhoto={list.customPhoto}
            />
            <div className="list-hero-overlay" />
            <div className={`privacy-badge ${hasCollaborators ? 'collaborative' : ''}`}>
              {getPrivacyIcon()}
            </div>
            {isPinned && (
              <div className="pinned-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16 4h2v2h-1l-1 4 1.5 1.5L17 13h-3v7l-1 2-1-2v-7H9l-.5-1.5L10 10l-1-4H8V4h8z"/>
                </svg>
              </div>
            )}
            {list.hasItinerary && (
              <button
                className="trip-plan-badge"
                onClick={handleTripClick}
                aria-label="View trip plan"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <span>Trip</span>
              </button>
            )}
          </div>
        )}

        <div className="list-preview-content">
          <h3 className="list-preview-title">{list.title || list.name}</h3>
          <div className="list-preview-subtitle">
            {locationDisplay && <span>{locationDisplay}</span>}
            {locationDisplay && categoryDisplay && <span className="meta-bullet">•</span>}
            {categoryDisplay && <span>{categoryDisplay}</span>}
          </div>
          <div className="list-preview-meta">
            <span className="meta-places">
              {placeCount} {placeLabel}
            </span>
            <span className="meta-bullet">•</span>
            <span className="meta-progress">{percentComplete}% visited</span>
            {!isPrivate && (
              <>
                <span className="meta-bullet">•</span>
                <span className="meta-followers">{followersCount} {followersCount === 1 ? 'follower' : 'followers'}</span>
              </>
            )}
            {timeSince && (
              <>
                <span className="meta-bullet">•</span>
                <span className="meta-time">{timeSince}</span>
              </>
            )}
          </div>
        </div>

        {/* Compact view: badges on the right side */}
        {isCompactView && !isReorderMode && (
          <div className="compact-badges">
            <span className={`compact-privacy-badge ${hasCollaborators ? 'collaborative' : ''}`}>
              {getPrivacyIcon()}
            </span>
            {isPinned && (
              <span className="compact-pinned-badge" title="Pinned">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16 4h2v2h-1l-1 4 1.5 1.5L17 13h-3v7l-1 2-1-2v-7H9l-.5-1.5L10 10l-1-4H8V4h8z"/>
                </svg>
              </span>
            )}
            {list.hasItinerary && (
              <button
                className="compact-trip-badge"
                onClick={handleTripClick}
                title="View trip plan"
                aria-label="View trip plan"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DraggableListCard;
